import { BRIDGE_SEATS, type BridgeControllers, type BridgeSeat as Seat, SINGLE_PLAYER } from '../bridge/seats.js';
import { type Card, type Rank, fullDeck, sameCard, sortHand } from '../trick/cards.js';
import { seededRandom, shuffled } from '../trick/rng.js';
import { type MeldShape, handValue, layOffOnto, validateMeld } from './melds.js';

export type RummySeat = Seat;

export class IllegalRummyMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalRummyMoveError';
  }
}

/** `draw`: take a card; `play`: lay down melds and lay cards off, then discard one; `done`: the hand is over. */
export type RummyPhase = 'draw' | 'play' | 'done';

/** A meld on the table. Anyone may lay a card off onto it. */
export interface TableMeld extends MeldShape {
  readonly id: number;
  /** Who laid it down. */
  readonly owner: Seat;
}

/**
 * Basic Rummy, 2 to 4 players. Draw the top of the stock or the top of the discard pile, lay down melds (sets and runs) and lay cards
 * off onto any meld on the table, then discard one card. The first player to get rid of every card wins the hand and scores the value
 * of the cards left in the other hands. No stakes: points are only a score.
 *
 * The state holds every hand and the stock; clients and robots read only `rummyViewFor` (view.ts).
 */
export interface RummyGame {
  readonly seed: number;
  readonly dealer: Seat;
  /** The seats in play, clockwise (2 to 4 of the four table seats). */
  readonly seats: readonly Seat[];
  readonly controllers: BridgeControllers;
  /** Whether the ace may also be played high (Q-K-A); it then scores 15 instead of 1. */
  readonly aceHigh: boolean;
  /** Lowest rank in the deck: 2 full, 8 Big Cards (28 cards, runs wrap around). */
  readonly minRank: Rank;
  /** Runs wrap around the ring of ranks (Q-K-A-2, or Q-K-A-8 with Big Cards). */
  readonly wrap: boolean;
  readonly hands: Readonly<Partial<Record<Seat, readonly Card[]>>>;
  /** Face-down cards, next card last. */
  readonly stock: readonly Card[];
  /** The face-up pile, the top card last. Everyone may look at all of it. */
  readonly discard: readonly Card[];
  readonly melds: readonly TableMeld[];
  readonly phase: RummyPhase;
  readonly turn: Seat;
  /** The card the seat to play took from the discard pile this turn: it may not be put straight back. */
  readonly takenFromDiscard: Card | null;
  /** How many times the discard pile was turned over to make a new stock. */
  readonly recycles: number;
  readonly nextMeldId: number;
  readonly winner: Seat | null;
  /** True when nobody could go out and the hand was stopped (the stock ran dry for good). */
  readonly blocked: boolean;
}

export interface NewRummyOptions {
  readonly seed: number;
  /** 2 to 4 seats, in table order. Defaults to all four. */
  readonly seats?: readonly Seat[];
  readonly dealer?: Seat;
  readonly controllers?: BridgeControllers;
  readonly aceHigh?: boolean;
  /** Deck's lowest rank, default 2. With 8 the ace is worth 15 unless `aceHigh` says otherwise. */
  readonly minRank?: Rank;
  /** Runs wrap around the ring of ranks. Default: off for the full deck, on for Big Cards. */
  readonly wrap?: boolean;
}

/**
 * Ten cards each when two play, seven when three or four do. With Big Cards (28 cards) hands are smaller so the stock
 * is not used up by the deal: 8, 6 and 5 for two, three and four players.
 */
export const rummyHandSize = (players: number, minRank: Rank = 2): number =>
  minRank >= 8 ? (players === 2 ? 8 : players === 3 ? 6 : 5) : players === 2 ? 10 : 7;

/** The discard pile is turned over to make a new stock at most this many times; after that a dry stock ends the hand. */
export const MAX_RECYCLES = 3;

const nextSeat = (seats: readonly Seat[], seat: Seat): Seat => seats[(seats.indexOf(seat) + 1) % seats.length] as Seat;
const handOf = (game: RummyGame, seat: Seat): readonly Card[] => game.hands[seat] ?? [];

export function newRummyGame(opts: NewRummyOptions): RummyGame {
  const seats = (opts.seats ?? BRIDGE_SEATS).filter((s, i, a) => a.indexOf(s) === i);
  if (seats.length < 2 || seats.length > 4) throw new IllegalRummyMoveError(`Rummy is for 2 to 4 players, not ${seats.length}`);
  const dealer = opts.dealer && seats.includes(opts.dealer) ? opts.dealer : (seats[0] as Seat);
  const minRank = opts.minRank ?? 2;
  const deck = shuffled(fullDeck(minRank), seededRandom(opts.seed));
  const hands: Record<string, Card[]> = Object.fromEntries(seats.map((s) => [s, [] as Card[]]));
  const order = [...seats.slice(seats.indexOf(dealer) + 1), ...seats.slice(0, seats.indexOf(dealer) + 1)];
  for (let r = 0; r < rummyHandSize(seats.length, minRank); r++) for (const s of order) (hands[s] as Card[]).push(deck.pop() as Card);
  const first = deck.pop() as Card;
  return {
    seed: opts.seed,
    dealer,
    seats,
    controllers: opts.controllers ?? SINGLE_PLAYER,
    aceHigh: opts.aceHigh ?? minRank >= 8,
    minRank,
    wrap: opts.wrap ?? minRank >= 8,
    hands: Object.fromEntries(seats.map((s) => [s, sortHand(hands[s] as Card[])])),
    stock: deck,
    discard: [first],
    melds: [],
    phase: 'draw',
    turn: nextSeat(seats, dealer),
    takenFromDiscard: null,
    recycles: 0,
    nextMeldId: 1,
    winner: null,
    blocked: false,
  };
}

/** What the meld functions take for the wrap-around option: the deck's lowest rank when runs wrap, else undefined. */
export const wrapFromOf = (game: { readonly wrap: boolean; readonly minRank: Rank }): Rank | undefined => (game.wrap ? game.minRank : undefined);

export const topDiscard = (game: RummyGame): Card | null => game.discard[game.discard.length - 1] ?? null;

/** Can the stock be drawn from: it has a card, or the pile under the top card can be turned over (a limited number of times). */
export const canDrawStock = (game: RummyGame): boolean =>
  game.phase === 'draw' && (game.stock.length > 0 || (game.discard.length > 1 && game.recycles < MAX_RECYCLES));

export const canDrawDiscard = (game: RummyGame): boolean => game.phase === 'draw' && game.discard.length > 0;

/** The cards the seat to play may discard now: any, except the card just taken from the pile (unless it is the only card left). */
export function rummyDiscardable(game: RummyGame): Card[] {
  if (game.phase !== 'play') return [];
  const hand = handOf(game, game.turn);
  const taken = game.takenFromDiscard;
  if (!taken || hand.length === 1) return [...hand];
  return hand.filter((c) => !sameCard(c, taken));
}

function endBlocked(game: RummyGame): RummyGame {
  const best = game.seats.reduce((a, b) => (handValue(handOf(game, b), game.aceHigh) < handValue(handOf(game, a), game.aceHigh) ? b : a));
  return { ...game, phase: 'done', winner: best, blocked: true };
}

/** Begin the next seat's turn; a dry stock that can no longer be refilled ends the hand. */
function beginTurn(game: RummyGame): RummyGame {
  const next: RummyGame = { ...game, turn: nextSeat(game.seats, game.turn), phase: 'draw', takenFromDiscard: null };
  if (next.stock.length === 0 && (next.discard.length <= 1 || next.recycles >= MAX_RECYCLES)) return endBlocked(next);
  return next;
}

const out = (game: RummyGame, seat: Seat, hands: RummyGame['hands']): RummyGame => ({ ...game, hands, phase: 'done', winner: seat, takenFromDiscard: null });

/** Draw the top of the stock or of the discard pile; the turn then moves to the play phase. */
export function rummyDraw(game: RummyGame, from: 'stock' | 'discard'): RummyGame {
  if (game.phase !== 'draw') throw new IllegalRummyMoveError('you cannot draw now');
  const seat = game.turn;
  if (from === 'discard') {
    if (!canDrawDiscard(game)) throw new IllegalRummyMoveError('the discard pile is empty');
    const card = game.discard[game.discard.length - 1] as Card;
    return {
      ...game,
      hands: { ...game.hands, [seat]: sortHand([...handOf(game, seat), card]) },
      discard: game.discard.slice(0, -1),
      phase: 'play',
      takenFromDiscard: card,
    };
  }
  if (!canDrawStock(game)) throw new IllegalRummyMoveError('the stock is empty');
  let stock = [...game.stock];
  let discard = [...game.discard];
  let recycles = game.recycles;
  if (stock.length === 0) {
    // Turn the pile under its top card over to make a new stock, shuffled from the game's seed so a seeded game replays exactly.
    const keep = discard[discard.length - 1] as Card;
    stock = shuffled(discard.slice(0, -1), seededRandom(game.seed + discard.length * 31 + recycles * 7919));
    discard = [keep];
    recycles += 1;
  }
  const card = stock.pop() as Card;
  return { ...game, hands: { ...game.hands, [seat]: sortHand([...handOf(game, seat), card]) }, stock, discard, recycles, phase: 'play', takenFromDiscard: null };
}

/** Lay down a meld (a set or a run) from your hand. Laying down your last cards wins the hand. */
export function rummyMeld(game: RummyGame, cards: readonly Card[]): RummyGame {
  if (game.phase !== 'play') throw new IllegalRummyMoveError('you can only lay down melds after drawing');
  const seat = game.turn;
  const hand = handOf(game, seat);
  if (!cards.every((c) => hand.some((h) => sameCard(h, c))) || new Set(cards.map((c) => `${c.suit}${c.rank}`)).size !== cards.length) {
    throw new IllegalRummyMoveError('those cards are not all in your hand');
  }
  const meld = validateMeld(cards, game.aceHigh, wrapFromOf(game));
  if (!meld) throw new IllegalRummyMoveError('that is not a set or a run');
  const rest = hand.filter((h) => !cards.some((c) => sameCard(h, c)));
  const hands = { ...game.hands, [seat]: rest };
  const next: RummyGame = { ...game, melds: [...game.melds, { ...meld, id: game.nextMeldId, owner: seat }], nextMeldId: game.nextMeldId + 1 };
  return rest.length === 0 ? out(next, seat, hands) : { ...next, hands };
}

/** Add one card from your hand to a meld on the table (yours or anyone's). */
export function rummyLayOff(game: RummyGame, card: Card, meldId: number): RummyGame {
  if (game.phase !== 'play') throw new IllegalRummyMoveError('you can only lay cards off after drawing');
  const seat = game.turn;
  const hand = handOf(game, seat);
  if (!hand.some((h) => sameCard(h, card))) throw new IllegalRummyMoveError('that card is not in your hand');
  const meld = game.melds.find((m) => m.id === meldId);
  if (!meld) throw new IllegalRummyMoveError('there is no such meld');
  const cards = layOffOnto(meld, card, game.aceHigh, wrapFromOf(game));
  if (!cards) throw new IllegalRummyMoveError('that card does not fit that meld');
  const rest = hand.filter((h) => !sameCard(h, card));
  const hands = { ...game.hands, [seat]: rest };
  const next: RummyGame = { ...game, melds: game.melds.map((m) => (m.id === meldId ? { ...m, cards } : m)) };
  return rest.length === 0 ? out(next, seat, hands) : { ...next, hands };
}

/** Discard one card to end your turn. Discarding your last card wins the hand. */
export function rummyDiscard(game: RummyGame, card: Card): RummyGame {
  if (game.phase !== 'play') throw new IllegalRummyMoveError('you can only discard after drawing');
  if (!rummyDiscardable(game).some((c) => sameCard(c, card))) throw new IllegalRummyMoveError('you cannot discard that card');
  const seat = game.turn;
  const rest = handOf(game, seat).filter((h) => !sameCard(h, card));
  const hands = { ...game.hands, [seat]: rest };
  const next: RummyGame = { ...game, hands, discard: [...game.discard, card] };
  return rest.length === 0 ? out(next, seat, hands) : beginTurn(next);
}

export interface RummyResult {
  readonly winner: Seat;
  /** The winner's score for the hand: the value of the cards left in the other hands (a blocked table: the others' minus the winner's own). */
  readonly points: number;
  /** How many cards each seat still holds. */
  readonly cardsLeft: Readonly<Partial<Record<Seat, number>>>;
  readonly blocked: boolean;
}

export function rummyResult(game: RummyGame): RummyResult | null {
  if (game.phase !== 'done' || !game.winner) return null;
  const winner = game.winner;
  const others = game.seats.filter((s) => s !== winner).reduce((a, s) => a + handValue(handOf(game, s), game.aceHigh), 0);
  const mine = handOf(game, winner);
  return {
    winner,
    points: Math.max(0, others - (game.blocked ? handValue(mine, game.aceHigh) : 0)),
    cardsLeft: Object.fromEntries(game.seats.map((s) => [s, handOf(game, s).length])),
    blocked: game.blocked,
  };
}

/** Scores add up over hands; the first to reach the target wins the game. */
export const RUMMY_TARGET = 100;
export type RummyTotals = Readonly<Partial<Record<Seat, number>>>;

export function addRummyHand(totals: RummyTotals, result: RummyResult): RummyTotals {
  return { ...totals, [result.winner]: (totals[result.winner] ?? 0) + result.points };
}

/** The seat that has reached the target with the highest total, or null while the game goes on. */
export function rummyGameWinner(totals: RummyTotals, target = RUMMY_TARGET): Seat | null {
  const entries = Object.entries(totals).filter((e): e is [Seat, number] => typeof e[1] === 'number');
  const best = Math.max(0, ...entries.map(([, v]) => v));
  if (best < target) return null;
  const leaders = entries.filter(([, v]) => v === best);
  return leaders.length === 1 ? (leaders[0] as [Seat, number])[0] : null; // a tie at the top plays another hand
}
